import type { RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { RefinanceState } from '@/store/slices/refinanceSlice';
import { calculateHELOANClosingDisclosure } from '@/utils/calculator/HELOAN';
import { calculateHELOCClosingDisclosure } from '@/utils/calculator/HELOC';
import { calculateSecondMortgageClosingDisclosure } from '@/utils/calculator/SecondMortgage';
import {
  calculateBridgeRefinanceClosingDisclosure,
  calculateCommercialClosingDisclosure,
  calculateConventionalClosingDisclosure,
  calculateDefaultClosingDisclosure,
  calculateDSCRClosingDisclosure,
  calculateFHAClosingDisclosure,
  calculateFixAndFlipClosingDisclosure,
  calculateUSDAClosingDisclosure,
  calculateVAClosingDisclosure,
} from '@/utils/refinanceCalculator';

export type RefinanceScenarioId =
  | 'lower_payment'
  | 'faster_payoff'
  | 'cash_access';

export interface RefinanceScenarioResult {
  id: RefinanceScenarioId;
  title: string;
  subtitle: string;
  loanTermYears: number;
  newLoanAmount: number;
  estimation: Record<string, unknown> & {
    piti?: number;
    closingCosts?: number;
    interestRate?: number;
    principalAndInterest?: number;
    loanTerm?: number;
    loanAmount?: number;
    totalInterest?: number;
  };
  monthlyPiti: number;
  monthlySavings: number;
  closingCosts: number;
  breakEvenMonths: number | null;
  lifetimeInterest: number;
}

type EstimationInput = {
  offerPrice: number;
  listPrice: number;
  downPayment: number;
  zipCode: number;
  hoaFees: number;
  commission: number;
  marketInterestRate: number;
  creditScore: number;
  loanTerm: number;
  currentLoanBalance: number;
  requestedLoanAmount: number;
  requestedCreditLine: number;
};

function newLoanAmountForScenario(
  refinance: RefinanceState,
  opts: {
    refiType: RefinanceState['selection']['refiType'];
    cashOutAmount: number;
  }
): number {
  const { refiType, cashOutAmount } = opts;
  if (['heloc', 'heloan', 'second_mortgage'].includes(refiType)) {
    return (
      refinance.newLoan.requestedLoanAmount || refinance.currentLoan.balance
    );
  }
  if (refiType === 'cash_out') {
    return refinance.currentLoan.balance + (cashOutAmount || 0);
  }
  return refinance.currentLoan.balance;
}

function buildEstimationInput(
  refinance: RefinanceState,
  newLoanAmount: number,
  loanTermYears: number
): EstimationInput {
  const equityLineOrLoan =
    refinance.newLoan.requestedLoanAmount || refinance.currentLoan.balance;
  return {
    offerPrice: refinance.property.value,
    listPrice: refinance.property.value,
    downPayment: Math.max(0, refinance.property.value - newLoanAmount),
    zipCode: Number(refinance.property.zipCode) || 0,
    hoaFees: refinance.currentLoan.escrowHoa,
    commission: 0,
    marketInterestRate: refinance.currentLoan.rate,
    creditScore: refinance.newLoan.creditScore,
    loanTerm: loanTermYears,
    currentLoanBalance: refinance.currentLoan.balance,
    requestedLoanAmount: equityLineOrLoan,
    requestedCreditLine: equityLineOrLoan,
  };
}

function runCalculator(
  program: USER_LOAN_PROGRAM,
  input: EstimationInput,
  state: RootState,
  refinance: RefinanceState
) {
  switch (program) {
    case USER_LOAN_PROGRAM.CONVENTIONAL:
      return calculateConventionalClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.FHA:
      return calculateFHAClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.VA:
      return calculateVAClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.USDA:
      return calculateUSDAClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.DSCR:
      return calculateDSCRClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.FIX_AND_FLIP:
      return calculateFixAndFlipClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.COMMERCIAL:
      return calculateCommercialClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.BRIDGE: {
      const totalLoan = input.offerPrice - input.downPayment;
      const existingLoanBalance = refinance.currentLoan.balance;
      const cashOutAmount = Math.max(0, totalLoan - existingLoanBalance);
      const loanTermMonths = Math.min(24, Math.max(6, input.loanTerm * 12));
      return calculateBridgeRefinanceClosingDisclosure(
        {
          currentPropertyValue: input.offerPrice,
          existingLoanBalance,
          cashOutAmount,
          zipCode: input.zipCode,
          hoaFees: input.hoaFees,
          marketInterestRate: input.marketInterestRate,
          creditScore: input.creditScore,
          loanTerm: loanTermMonths,
        },
        state
      );
    }
    case USER_LOAN_PROGRAM.HELOC:
      return calculateHELOCClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.HELOAN:
      return calculateHELOANClosingDisclosure(input, state);
    case USER_LOAN_PROGRAM.SECOND_MORTGAGE:
      return calculateSecondMortgageClosingDisclosure(input, state);
    default:
      return calculateDefaultClosingDisclosure(input, state);
  }
}

function approxLifetimeInterest(
  principalAndInterest: number,
  loanTermYears: number,
  loanAmount: number
): number {
  const n = loanTermYears * 12;
  return Math.max(0, principalAndInterest * n - loanAmount);
}

/**
 * Computes three refinance scenarios vs current PITI. Rate tracking on the summary page
 * uses the "primary" single-estimate path; scenarios here are for side-by-side advice.
 */
export function computeRefinanceScenarios(
  refinance: RefinanceState,
  estimationState: RootState,
  program: USER_LOAN_PROGRAM
): RefinanceScenarioResult[] {
  const propertyValue = refinance.property.value;
  const balance = refinance.currentLoan.balance;
  const currentMonthlyTotal =
    refinance.currentLoan.monthlyPayment +
    refinance.currentLoan.escrowTaxes +
    refinance.currentLoan.escrowInsurance +
    refinance.currentLoan.escrowHoa;

  const maxLoan80 = propertyValue > 0 ? propertyValue * 0.8 : 0;
  const maxCashOut = Math.max(
    0,
    Math.min(
      maxLoan80 - balance,
      refinance.newLoan.cashOutAmount > 0
        ? refinance.newLoan.cashOutAmount
        : Math.min(75000, Math.max(0, maxLoan80 - balance) * 0.5)
    )
  );

  const baseTerm = Math.min(
    30,
    Math.max(10, refinance.newLoan.termYears || 30)
  );

  const scenarios: {
    id: RefinanceScenarioId;
    title: string;
    subtitle: string;
    termYears: number;
    refiType: RefinanceState['selection']['refiType'];
    cashOut: number;
  }[] = [
    {
      id: 'lower_payment',
      title: 'Option A — Lower payment',
      subtitle: 'Longest term to minimize monthly PITI',
      termYears: 30,
      refiType: 'rate_and_term',
      cashOut: 0,
    },
    {
      id: 'faster_payoff',
      title: 'Option B — Faster payoff',
      subtitle: 'Shorter amortization; higher payment, less lifetime interest',
      termYears: Math.min(baseTerm, 15),
      refiType: 'rate_and_term',
      cashOut: 0,
    },
    {
      id: 'cash_access',
      title: 'Option C — Cash access',
      subtitle: 'Cash-out within a typical 80% LTV cap (illustrative)',
      termYears: 30,
      refiType: 'cash_out',
      cashOut: maxCashOut,
    },
  ];

  return scenarios.map((s) => {
    const tempRefinance: RefinanceState = {
      ...refinance,
      selection: {
        ...refinance.selection,
        refiType: s.refiType,
      },
      newLoan: {
        ...refinance.newLoan,
        cashOutAmount: s.cashOut,
      },
    };

    const newLoanAmt = newLoanAmountForScenario(tempRefinance, {
      refiType: s.refiType,
      cashOutAmount: s.cashOut,
    });

    const input = buildEstimationInput(tempRefinance, newLoanAmt, s.termYears);
    const estimation = runCalculator(
      program,
      input,
      estimationState,
      tempRefinance
    ) as RefinanceScenarioResult['estimation'];

    const monthlyPiti = Number(estimation?.piti ?? 0);
    const closingCosts = Number(estimation?.closingCosts ?? 0);
    const monthlySavings = currentMonthlyTotal - monthlyPiti;
    const breakEvenMonths =
      monthlySavings > 0 && closingCosts > 0
        ? Math.round(closingCosts / monthlySavings)
        : null;

    const pAndI = Number(estimation?.principalAndInterest ?? 0);
    const loanAmt = Number(estimation?.loanAmount ?? newLoanAmt);
    const termYears = Number(estimation?.loanTerm ?? s.termYears);
    let lifetimeInterest = approxLifetimeInterest(pAndI, termYears, loanAmt);
    if (
      estimation?.totalInterest != null &&
      Number(estimation.totalInterest) > 0
    ) {
      lifetimeInterest = Number(estimation.totalInterest);
    }

    return {
      id: s.id,
      title: s.title,
      subtitle: s.subtitle,
      loanTermYears: s.termYears,
      newLoanAmount: newLoanAmt,
      estimation,
      monthlyPiti,
      monthlySavings,
      closingCosts,
      breakEvenMonths,
      lifetimeInterest,
    };
  });
}
